import { useState } from "react";

const DISMISSED_KEY = "pia_scope_banner_dismissed";

interface ScopeBannerProps {
	/** Whether the current OAuth token covers all required scopes. */
	scopesValid: boolean;
	/** Called when the user clicks "Log out". */
	onLogout: () => void;
}

/**
 * Amber dismissible banner shown when the authenticated user's OAuth token
 * does not include all scopes required for write-back actions (Phase 7).
 *
 * Renders only when:
 *  - scopesValid is false, AND
 *  - the user has not dismissed it this session (sessionStorage key absent)
 *
 * Dismiss persists for the browser session only — a fresh tab re-shows the
 * banner until the user re-authenticates.
 */
export default function ScopeBanner({ scopesValid, onLogout }: ScopeBannerProps) {
	const [dismissed, setDismissed] = useState<boolean>(
		() => sessionStorage.getItem(DISMISSED_KEY) === "true",
	);

	// Do not render if scopes are valid or already dismissed.
	if (scopesValid || dismissed) return null;

	function handleDismiss() {
		sessionStorage.setItem(DISMISSED_KEY, "true");
		setDismissed(true);
	}

	return (
		<div
			className="scope-banner"
			role="alert"
			aria-live="polite"
			aria-atomic="true"
		>
			<div className="scope-banner-body">
				<span className="scope-banner-icon" aria-hidden="true">&#9888;</span>
				<span>
					PIA has new capabilities. Please log out and log back in to enable
					task creation and write actions.
				</span>
			</div>
			<div className="scope-banner-actions">
				<button
					type="button"
					className="scope-banner-btn scope-banner-btn--logout"
					onClick={onLogout}
				>
					Log out
				</button>
				<button
					type="button"
					className="scope-banner-btn scope-banner-btn--dismiss"
					onClick={handleDismiss}
					aria-label="Dismiss this notification"
				>
					Dismiss
				</button>
			</div>
		</div>
	);
}
